import { useMemo } from "react";
import {
  ArrowLeftRight,
  Camera,
  Check,
  HelpCircle,
  TrendingUp,
  Undo2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import InfoSheet from "../common/InfoSheet";
import GlucoseCurveComparisonPanel from "./GlucoseCurveComparisonPanel";
import type { GlucoseCurveChartPoint } from "./GlucoseCurveComparisonPanel";

const PREVIEW_NO_ACTION_PEAK = 178;
const PREVIEW_WITH_ACTION_PEAK = 144;
const PREVIEW_DROP = PREVIEW_NO_ACTION_PEAK - PREVIEW_WITH_ACTION_PEAK;

const PREVIEW_CHART_DATA: GlucoseCurveChartPoint[] = [
  { t: 0, noAction: 98, withAction: 98 },
  { t: 15, noAction: 125, withAction: 118 },
  { t: 30, noAction: 158, withAction: 132 },
  { t: 45, noAction: 178, withAction: 144 },
  { t: 60, noAction: 165, withAction: 138 },
  { t: 90, noAction: 128, withAction: 118 },
  { t: 120, noAction: 108, withAction: 102 },
];

const FEATURES = [
  {
    title: "Predict",
    sub: "2-hour BGL curve",
    icon: TrendingUp,
  },
  {
    title: "Optimize",
    sub: "Eating order",
    icon: ArrowLeftRight,
  },
  {
    title: "Bend",
    sub: "The spike",
    icon: Undo2,
  },
] as const;

export interface OnboardingHomeSectionProps {
  onLogFirstMeal?: () => void;
  onHelp?: () => void;
}

const formatOnboardingDateLine = (date: Date = new Date()): string => {
  const weekday = date
    .toLocaleDateString("en-US", { weekday: "short" })
    .toUpperCase();
  const dayMonth = date
    .toLocaleDateString("en-US", { day: "2-digit", month: "short" })
    .toUpperCase()
    .replace(",", "");
  return `${weekday} · ${dayMonth} · WELCOME`;
};

const OnboardingHomeSection = ({
  onLogFirstMeal,
  onHelp,
}: OnboardingHomeSectionProps) => {
  const chartData = useMemo(() => PREVIEW_CHART_DATA, []);

  const handleLogMeal = () => {
    if (onLogFirstMeal) {
      onLogFirstMeal();
      return;
    }
    window.dispatchEvent(new CustomEvent("glucera-open-meal-camera"));
  };

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between px-0.5">
        <p className="text-compact-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
          {formatOnboardingDateLine()}
        </p>
        <InfoSheet
          title="How Bite Balance works"
          ariaLabel="How Bite Balance works"
          onOpenChange={(open) => {
            if (open) onHelp?.();
          }}
          trigger={
            <button
              type="button"
              aria-label="How Bite Balance works"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-border/70 bg-card text-muted-foreground transition-colors hover:bg-muted/60"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          }
        >
          <div className="space-y-3">
            <ol className="space-y-2.5 text-sm leading-relaxed text-foreground/90">
              <li className="flex gap-2">
                <span className="font-bold text-blue-600 dark:text-blue-400">1.</span>
                <span>
                  <span className="font-semibold text-foreground">Snap your meal.</span> Take a
                  photo — no CGM or manual logging of grams needed.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-blue-600 dark:text-blue-400">2.</span>
                <span>
                  <span className="font-semibold text-foreground">See your curve.</span> We
                  predict your 2-hour blood glucose response before you eat.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-blue-600 dark:text-blue-400">3.</span>
                <span>
                  <span className="font-semibold text-foreground">Bend the spike.</span> Get
                  simple actions — eating order or a short walk — to flatten it.
                </span>
              </li>
            </ol>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Tap <span className="font-semibold text-foreground">Log your first meal</span> below
              to get started.
            </p>
          </div>
        </InfoSheet>
      </div>

      <Card className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-md">
        <CardContent className="space-y-5 p-5 sm:p-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-2.5 py-1 text-2xs font-bold uppercase tracking-[0.1em] text-orange-800 ring-1 ring-orange-200/90 dark:bg-orange-950/40 dark:text-orange-200 dark:ring-orange-800/50">
            <Check className="h-3 w-3 shrink-0" strokeWidth={3} />
            No CGM required
          </span>

          <div className="space-y-3">
            <h2 className="text-title1 font-semibold leading-[1.18] tracking-tight text-foreground sm:text-large-title">
              See what your meal will do to your blood sugar —{" "}
              <span className="italic">before it happens.</span>
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Take a photo. Bite Balance predicts your 2-hour glucose curve
              and shows you exactly how to bend it.
            </p>
          </div>

          <Card className="overflow-hidden rounded-xl border border-border/60 bg-muted/20 shadow-sm">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-2xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  Preview
                </span>
                <span className="text-2xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  Example · Lunch bowl
                </span>
              </div>

              <GlucoseCurveComparisonPanel
                chartData={chartData}
                noActionPeak={PREVIEW_NO_ACTION_PEAK}
                withActionPeak={PREVIEW_WITH_ACTION_PEAK}
                dropMgDl={PREVIEW_DROP}
                activityLabel="15-min walk"
                chartHeightClass="h-36"
                showPeakComparison={false}
                withActionStroke="#22c55e"
                noActionStrokeDash="5 4"
                idleChartMode
                compactLegend
              />

              <div className="flex flex-wrap items-center justify-center gap-2 border-t border-border/50 pt-3 text-center">
                <span className="text-lg font-black tabular-nums text-red-500 line-through decoration-2">
                  {PREVIEW_NO_ACTION_PEAK}
                </span>
                <span className="text-compact-xs font-bold uppercase tracking-wide text-muted-foreground">
                  No action
                </span>
                <span className="text-muted-foreground">→</span>
                <span className="text-lg font-black tabular-nums text-emerald-600">
                  {PREVIEW_WITH_ACTION_PEAK}
                </span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-compact-xs font-bold uppercase tracking-wide text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                  + 15-min walk
                </span>
              </div>
            </CardContent>
          </Card>

          <Button
            type="button"
            onClick={handleLogMeal}
            className="h-12 w-full gap-2 rounded-2xl bg-zinc-900 text-sm font-semibold text-amber-200 shadow-md hover:bg-zinc-800"
          >
            <Camera className="h-5 w-5 shrink-0" strokeWidth={2.25} />
            Log your first meal
          </Button>

          <div className="grid grid-cols-3 gap-2">
            {FEATURES.map(({ title, sub, icon: Icon }) => (
              <div
                key={title}
                className="flex flex-col items-center rounded-xl border border-border/60 bg-muted/15 px-2 py-3 text-center"
              >
                <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-card ring-1 ring-border/60">
                  <Icon className="h-4 w-4 text-foreground/80" strokeWidth={2} />
                </div>
                <p className="text-compact-sm font-bold text-foreground">{title}</p>
                <p className="mt-0.5 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {sub}
                </p>
              </div>
            ))}
          </div>

          <p className="text-center text-2xs font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
            BBS-1554 model · Built for prediabetes, T2D &amp; metabolic health
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingHomeSection;
